import { checkRecipientPermissionService } from '../services/checkRecipientPermission';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getFileProviderAuthHeaderValue } from 'owa-fileprovider-services';
import RecipientWellWithFindControlViewState from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentPermissionLevel from 'owa-service/lib/contract/AttachmentPermissionLevel';
import { SharingTipRecipientInfo } from 'owa-sharing-data';
import { GetPermissionInfoStatus, RecipientPermissionInfo } from 'owa-sharing-data';
import { SharingData } from 'owa-sharing-data';
import { format } from 'owa-strings';
import { trace } from 'owa-trace';
import {
    getSharingLink,
    getSharingDataFromLink,
    lazyGetSharingTipRecipientInfo,
    lazyUpdateHasDLRecipient,
    lazyAddRecipientsIntoLink,
    lazyRemoveRecipientsFromLink,
} from 'owa-link-data';

const RECIPIENTS_MAX_NUM_PER_REQUEST: number = 30;

export async function tryCheckRecipientPermissionForOneLink(
    linkId: string,
    recipientWells: RecipientWellWithFindControlViewState[],
    fromAddress: string
) {
    if (!isFeatureEnabled('doc-recipient-permission')) {
        return;
    }

    const getSharingTipRecipientInfo = await lazyGetSharingTipRecipientInfo.import();
    const recipientInfos: SharingTipRecipientInfo[] = getSharingTipRecipientInfo(
        recipientWells,
        fromAddress
    );
    tryCheckRecipientPermission(linkId, recipientInfos);
}

export async function tryCheckRecipientPermission(
    linkId: string,
    recipientInfos: SharingTipRecipientInfo[]
) {
    trace.info('tryCheckRecipientPermission');
    const sharingData: SharingData = getSharingDataFromLink(linkId);
    const linkUrl = getSharingLink(linkId).url;
    if (
        sharingData.sharingInfo.getSharingInfoSucceeded &&
        sharingData.currentPermissionLevel === AttachmentPermissionLevel.None
    ) {
        // If we found DL in the recipients, we should not try to check recipient permission for the link.
        const hasDL = recipientInfos.some(function(recipientInfo) {
            return recipientInfo.totalMemberCount > 1;
        });

        const updateHasDLRecipient = await lazyUpdateHasDLRecipient.import();

        if (hasDL) {
            updateHasDLRecipient(linkId, true);
            return;
        }

        updateHasDLRecipient(linkId, false);
        const recipientsInLinkStore: RecipientPermissionInfo[] =
            sharingData.sharingInfo.recipientPermissionInfos;

        // find all new/removed recipients, update store and send checkRecipientPermission call
        const newRecipients: SharingTipRecipientInfo[] = recipientInfos.filter(function(
            recipientInfo
        ) {
            return !recipientsInLinkStore.some(
                recipient => recipient.emailAddress === recipientInfo.emailAddress
            );
        });
        const newRecipientPermissionInfos: RecipientPermissionInfo[] = [];
        newRecipients.forEach(newRecipient => {
            newRecipientPermissionInfos.push({
                emailAddress: newRecipient.emailAddress,
                name: newRecipient.name,
                getPermissionInfoStatus: GetPermissionInfoStatus.InProgress,
                hasAccess: false,
            });
        });

        const removedRecipients: RecipientPermissionInfo[] = recipientsInLinkStore.filter(function(
            recipientPermissionInfo
        ) {
            return !recipientInfos.some(
                recipient => recipient.emailAddress === recipientPermissionInfo.emailAddress
            );
        });

        // update store
        if (removedRecipients.length > 0) {
            trace.info(
                format(
                    'RecipientPermission: LinkId:{0}, remove {1} recipients ',
                    linkId,
                    removedRecipients.length
                )
            );
            const removeRecipientsFromLink = await lazyRemoveRecipientsFromLink.import();
            removeRecipientsFromLink(linkId, removedRecipients);
        }

        if (newRecipientPermissionInfos.length > 0) {
            trace.info(
                format(
                    'RecipientPermission: LinkId:{0}, add {1} recipients',
                    linkId,
                    newRecipientPermissionInfos.length
                )
            );
            const addRecipientsIntoLink = await lazyAddRecipientsIntoLink.import();
            addRecipientsIntoLink(linkId, newRecipientPermissionInfos);
            const token: string = await getFileProviderAuthHeaderValue(
                AttachmentDataProviderType.OneDrivePro,
                false,
                linkUrl
            );

            for (
                let i: number = 0;
                i < newRecipientPermissionInfos.length / RECIPIENTS_MAX_NUM_PER_REQUEST;
                i++
            ) {
                await checkRecipientPermissionService(
                    linkId,
                    linkUrl,
                    token,
                    newRecipientPermissionInfos.slice(
                        i * RECIPIENTS_MAX_NUM_PER_REQUEST,
                        (i + 1) * RECIPIENTS_MAX_NUM_PER_REQUEST
                    )
                );
            }
        }
    }
}
